import { useTranslation } from "react-i18next";

import { BackdropVideo } from "@/components/marketing/BackdropVideo";
import { Section, SectionHeader } from "@/components/marketing/Section";
import { cn } from "@/lib/cn";

// The compounding loop, drawn as a staircase: on desktop each card sits a
// step higher than the last (bottoms aligned, tops rising left → right) and
// the step numbers brighten toward full lime — the layout itself says
// "every pass climbs".
const STEP_RISE = ["lg:mt-12", "lg:mt-8", "lg:mt-4", "lg:mt-0"];
const STEP_NUMBER_TINT = ["text-primary/30", "text-primary/50", "text-primary/70", "text-primary"];

export function HowItWorks() {
  const { t } = useTranslation();
  const steps = t("howItWorks.steps", { returnObjects: true }) as {
    title: string;
    body: string;
  }[];

  return (
    <Section id="how-it-works" viewport>
      {/* Cosmic vortex (seamless-loop re-encode of the Mux source) —
          continuous motion for the compounding loop. */}
      <BackdropVideo src="/videos/cosmic-vortex-loop.mp4" />
      <div className="relative z-[1]">
        <SectionHeader title={t("howItWorks.title")} subtitle={t("howItWorks.subtitle")} />
        <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            return (
              <li
                key={step.title}
                className={cn(
                  "glass-card flex flex-col rounded-card p-6",
                  STEP_RISE[index],
                  // The payoff step gets the accent — this is where the loop compounds.
                  isLast && "border-primary/35 bg-primary/[0.04] hover:border-primary/50",
                )}
              >
                <span
                  className={cn(
                    "font-mono text-4xl font-medium tabular-nums",
                    STEP_NUMBER_TINT[index],
                  )}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 text-base font-semibold text-foreground">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                {isLast && (
                  <p className="mt-4 font-mono text-eyebrow uppercase text-primary">
                    {t("howItWorks.loopNote")}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </Section>
  );
}
